// components/ui/NeuralToast.tsx
"use client";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useCallback } from "react";

type ToastType = "success" | "error" | "warning" | "info";

interface ToastData {
  message: string;
  type: ToastType;
}

export function useNeuralToast() {
  const [toast, setToast] = useState<ToastData | null>(null);

  const showToast = useCallback((message: string, type: ToastType = "info") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const Toast = () => (
    <AnimatePresence>
      {toast && (
        <motion.div
          key="neural-toast"
          initial={{ y: 60, opacity: 0, scale: 0.9 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 40, opacity: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 18 }}
          className={`toast-glitch fixed bottom-8 right-8 z-[9999] px-6 py-3 
            font-rajdhani uppercase tracking-wider text-sm select-none pointer-events-none
            backdrop-blur-md border shadow-[0_0_20px_rgba(0,255,255,0.3)]
            ${getStyleForType(toast.type)}`}
        >
          {getIcon(toast.type)} {toast.message}
        </motion.div>
      )}
    </AnimatePresence>
  );

  return { showToast, Toast };
}

// ─────────────────────────────
// 🔮 Style helpers
// ─────────────────────────────
function getStyleForType(type: ToastType) {
  switch (type) {
    case "success":
      return "text-green-200 border-green-400/40 bg-green-900/30 shadow-[0_0_12px_#00ff9960]";
    case "error":
      return "text-pink-200 border-pink-500/40 bg-pink-900/30 shadow-[0_0_12px_#ff009980]";
    case "warning":
      return "text-amber-200 border-amber-400/40 bg-amber-900/30 shadow-[0_0_12px_#ffcc0080]";
    case "info":
    default:
      return "text-cyan-200 border-cyan-400/40 bg-cyan-900/30 shadow-[0_0_12px_#00ffff80]";
  }
}

function getIcon(type: ToastType) {
  switch (type) {
    case "success":
      return "✔";
    case "error":
      return "✖";
    case "warning":
      return "⚠";
    case "info":
    default:
      return "ℹ";
  }
}
